import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import {
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Skeleton,
} from '@mui/material'
import WebsiteJson from '../../Assets/JSON/MV.json'
import {
  IMAGE_BASE_PATH_DBRAIMT,
  IMAGE_BASE_PATH_COMMON,
} from '../../Config/Config'

const PLACEHOLDER_IMAGE = `${IMAGE_BASE_PATH_COMMON}Placeholder_Image.jpg`

const InfrastructureItem = React.memo(({ item, onOpenDialog }) => {
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const imgRef = useRef(null)

  const imageSrc = useMemo(
    () =>
      hasError
        ? PLACEHOLDER_IMAGE
        : item.ImageURL
        ? IMAGE_BASE_PATH_DBRAIMT + item.ImageURL
        : PLACEHOLDER_IMAGE,
    [item.ImageURL, hasError]
  )

  const handleLoad = useCallback(() => {
    setIsLoading(false)
  }, [])

  const handleError = useCallback((e) => {
    e.target.onerror = null
    e.target.src = PLACEHOLDER_IMAGE
    setIsLoading(false)
    setHasError(true)
  }, [])

  // Preload image when component mounts
  useEffect(() => {
    const img = new Image()
    img.src = imageSrc
    img.onload = () => {
      if (imgRef.current) {
        imgRef.current.src = imageSrc
        handleLoad()
      }
    }
    img.onerror = handleError
    return () => {
      img.onload = null
      img.onerror = null
    }
  }, [imageSrc, handleLoad, handleError])

  return (
    <Grid
      item
      xs={12}
      sm={6}
      md={4}
      lg={3}
      onClick={() => onOpenDialog(item)}
      sx={{ cursor: 'pointer' }}
    >
      <Box
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: 3,
          transition: 'transform 0.3s',
          '&:hover': {
            transform: 'scale(1.03)',
          },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {isLoading && (
          <Skeleton
            variant="rectangular"
            width="100%"
            height={200}
            animation="wave"
          />
        )}
        <img
          ref={imgRef}
          alt={item.Title}
          loading="lazy"
          style={{
            width: '100%',
            height: '200px',
            objectFit: 'cover',
            display: isLoading ? 'none' : 'block',
          }}
          onError={handleError}
        />
        <Box
          sx={{
            p: 1,
            backgroundColor: 'background.primaryBackground',
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="subtitle1" align="center" fontWeight={600}>
            {item.Title}
          </Typography>
        </Box>
      </Box>
    </Grid>
  )
})

const InfrastructureDialog = React.memo(({ open, item, onClose }) => {
  const [imgSrc, setImgSrc] = useState('')
  const [hasError, setHasError] = useState(false)

  React.useEffect(() => {
    if (item) {
      const src = item.ImageURL
        ? IMAGE_BASE_PATH_DBRAIMT + item.ImageURL
        : PLACEHOLDER_IMAGE
      setImgSrc(src)
      setHasError(false)
    }
  }, [item])

  const handleError = (e) => {
    e.target.onerror = null
    e.target.src = PLACEHOLDER_IMAGE
    setHasError(true)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      TransitionProps={{ unmountOnExit: true }}
    >
      {item && (
        <>
          <DialogTitle>{item.Title}</DialogTitle>
          <DialogContent>
            <img
              src={hasError ? PLACEHOLDER_IMAGE : imgSrc}
              alt={item.Title}
              loading="eager"
              style={{
                width: '100%',
                height: 290,
                objectFit: 'cover',
                backgroundColor: '#f5f5f5',
              }}
              onError={handleError}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Close</Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
})

const Infrastructure = () => {
  const [dialogState, setDialogState] = useState({
    open: false,
    selectedItem: null,
  })

  const infrastructureData = useMemo(() => WebsiteJson.Infrastructure || [], [])

  const handleOpenDialog = useCallback((item) => {
    setDialogState({ open: true, selectedItem: item })
  }, [])

  const handleCloseDialog = useCallback(() => {
    setDialogState((prev) => ({ ...prev, open: false }))
  }, [])

  return (
    <section className="container py-4">
      <Typography
        variant="h2"
        align="center"
        color="text.main"
        className="py-2"
        gutterBottom
      >
        Infrastructure of our College
      </Typography>

      <Grid container spacing={3}>
        {infrastructureData.map((item) => (
          <InfrastructureItem
            key={item.Title}
            item={item}
            onOpenDialog={handleOpenDialog}
          />
        ))}
      </Grid>

      <InfrastructureDialog
        open={dialogState.open}
        item={dialogState.selectedItem}
        onClose={handleCloseDialog}
      />
    </section>
  )
}

export default React.memo(Infrastructure)
